import {
  IconAlertCircle,
  IconDownload,
  IconLoader2,
  IconX,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  EmailAttachmentFetchError,
  fetchEmailAttachmentBlob,
} from "../../../api/queries/receivablesQueries";
import type { EmailAttachmentMeta } from "../../../utils/interfaces";
import {
  blobToDataUrl,
  createPreviewObjectUrl,
  getEmailAttachmentErrorMessage,
  isPreviewableAttachment,
  resolveAttachmentMimeType,
  toTypedPreviewFile,
} from "../../../utils/emailAttachments";
import { formatFileSize } from "../../../utils/utilsFunctions/functions";

type PreviewState =
  | { status: "loading" }
  | { status: "success"; previewUrl: string; mimeType: string }
  | {
      status: "error";
      message: string;
      reconnectMailbox?: boolean;
    };

type EmailAttachmentPreviewModalProps = {
  isOpen: boolean;
  messageId: string;
  attachmentId: string;
  meta: EmailAttachmentMeta;
  onClose: () => void;
};

export function EmailAttachmentPreviewModal({
  isOpen,
  messageId,
  attachmentId,
  meta,
  onClose,
}: EmailAttachmentPreviewModalProps) {
  const [previewState, setPreviewState] = useState<PreviewState>({
    status: "loading",
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const blobUrlRef = useRef<string | null>(null);

  const revokeBlobUrl = useCallback(() => {
    const url = blobUrlRef.current;
    if (url?.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
    blobUrlRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    revokeBlobUrl();

    if (!isPreviewableAttachment(meta)) {
      setPreviewState({
        status: "error",
        message:
          "Preview is not available for this file type. Use download instead.",
      });
      return;
    }

    setPreviewState({ status: "loading" });

    void (async () => {
      try {
        const blob = await fetchEmailAttachmentBlob(
          messageId,
          attachmentId,
          "inline",
          undefined,
          meta.filename,
        );
        if (cancelled) return;

        const filename = meta.filename ?? "attachment";
        const mimeType = resolveAttachmentMimeType(
          blob.type || meta.mimeType,
          filename,
          meta.mimeType,
        );
        const isPdfPreview =
          mimeType === "application/pdf" ||
          filename.toLowerCase().endsWith(".pdf");

        // Data URLs render reliably in iframes on HTTPS; blob URLs often trigger downloads.
        const previewUrl = isPdfPreview
          ? await blobToDataUrl(
              blob.type === mimeType
                ? blob
                : toTypedPreviewFile(blob, mimeType, filename),
            )
          : createPreviewObjectUrl(blob, mimeType, filename);
        if (!isPdfPreview) {
          blobUrlRef.current = previewUrl;
        }

        setPreviewState({ status: "success", previewUrl, mimeType });
      } catch (error: unknown) {
        if (cancelled) return;

        if (error instanceof EmailAttachmentFetchError) {
          setPreviewState({
            status: "error",
            message: error.message,
            reconnectMailbox: error.reconnectMailbox,
          });
          return;
        }

        setPreviewState({
          status: "error",
          message: "Could not load this attachment.",
        });
      }
    })();

    return () => {
      cancelled = true;
      revokeBlobUrl();
    };
  }, [isOpen, attachmentId, messageId, meta, retryKey, revokeBlobUrl]);

  const handleRetryPreview = useCallback(() => {
    setRetryKey((current) => current + 1);
  }, []);

  const handlePreviewError = useCallback(() => {
    setPreviewState({
      status: "error",
      message: "Could not load this attachment.",
    });
  }, []);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await fetchEmailAttachmentBlob(
        messageId,
        attachmentId,
        "attachment",
        undefined,
        meta.filename,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = meta.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof EmailAttachmentFetchError) {
        const { message, reconnectMailbox } = getEmailAttachmentErrorMessage(
          error.status,
          error.message,
        );
        setPreviewState({
          status: "error",
          message,
          reconnectMailbox,
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  const isImage =
    previewState.status === "success" &&
    previewState.mimeType.startsWith("image/");
  const isPdf =
    previewState.status === "success" &&
    (previewState.mimeType === "application/pdf" ||
      (meta.filename ?? "").toLowerCase().endsWith(".pdf"));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label="Close attachment preview"
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-attachment-preview-title"
        className="relative z-10 flex h-[min(90vh,48rem)] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <h2
              id="email-attachment-preview-title"
              className="truncate text-lg font-semibold text-slate-900"
              title={meta.filename}
            >
              {meta.filename}
            </h2>
            {meta.size != null ? (
              <p className="mt-0.5 text-sm text-slate-500">
                {formatFileSize(meta.size)}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={isDownloading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {isDownloading ? (
                <IconLoader2 size={16} className="animate-spin" />
              ) : (
                <IconDownload size={16} />
              )}
              Download
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col bg-slate-50 p-4 sm:p-6">
          {previewState.status === "loading" ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-slate-500">
              <IconLoader2 size={28} className="animate-spin text-slate-400" />
              Loading preview…
            </div>
          ) : previewState.status === "error" ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
              <IconAlertCircle
                size={32}
                className="text-amber-600"
                aria-hidden
              />
              <p className="max-w-md text-sm text-slate-700">
                {previewState.message}
              </p>
              {previewState.reconnectMailbox ? (
                <a
                  href="/settings?tab=email-sync"
                  className="text-sm font-medium text-[#3F6C61] hover:underline"
                >
                  Reconnect mailbox in settings
                </a>
              ) : null}
              <button
                type="button"
                onClick={handleRetryPreview}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Try again
              </button>
            </div>
          ) : isPdf ? (
            <iframe
              key={retryKey}
              src={`${previewState.previewUrl}#toolbar=0&navpanes=0`}
              title={`PDF preview: ${meta.filename}`}
              className="h-full min-h-80 w-full flex-1 rounded-lg border border-slate-200 bg-white"
              onError={handlePreviewError}
            />
          ) : isImage ? (
            <div className="flex flex-1 items-center justify-center overflow-auto">
              <img
                key={retryKey}
                src={previewState.previewUrl}
                alt={meta.filename}
                className="max-h-full max-w-full rounded-lg border border-slate-200 bg-white object-contain"
                onError={handlePreviewError}
              />
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-sm text-slate-600">
              <p>Preview is not available for this file type.</p>
              <button
                type="button"
                onClick={() => void handleDownload()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 font-medium text-slate-700 hover:bg-slate-50"
              >
                <IconDownload size={16} />
                Download file
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
